import type { Banner, BannerTranslation, BannerType } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'

import { mergeLeft } from 'merge-left-utils'


export type BannerTranslationState = {
  language_code: string
  file_id?: GQL_ID
  mobile_file_id?: GQL_ID
  bannerSlide?: {
    url?: string
  }
  bannerMobileSlide?: {
    url?: string
  }
  button_text?: string
}

export interface BannerFormState extends Omit<Banner, 'id' | 'bannerType' | 'i18n'> {
  id?: GQL_ID
  selectedBannerTypeId?: GQL_ID
  languages: string[]
  bannerType: Omit<BannerType, 'id'> & { id?: GQL_ID }
  i18n: BannerTranslationState[]
}

export const BANNER_TRANSLATION_DEFAULT_STATE: BannerTranslationState = {
  language_code: 'default',
  file_id: undefined,
  mobile_file_id: undefined,
  bannerSlide: { url: undefined },
  bannerMobileSlide: { url: undefined },
  button_text: ''
}

export const BANNER_DEFAULT_STATE: BannerFormState = {
  id: undefined,
  file_id: undefined,
  mobile_file_id: undefined,
  name: '',
  url: undefined,
  status: 0,
  optionOpenInNewWindow: false,
  optionOpenInBrowser: false,
  bannerSlide: { url: undefined },
  bannerMobileSlide: { url: undefined },
  activeFrom: undefined,
  activeUntil: undefined,
  bannerType: { id: undefined, label: '', code: '' },
  selectedBannerTypeId: undefined,
  i18n: [],
  languages: [ 'default', 'kk' ],
  has_button: false,
  button_color: 'green',
  button_text: ''
}

export const makeBannerFormState = (banner?: Banner): BannerFormState => {
  return mergeLeft<BannerFormState>(BANNER_DEFAULT_STATE, {
    ...banner,
    selectedBannerTypeId: banner?.bannerType.id
  })
}

export const mapStateToApiVariable = (state: BannerFormState): any => ({
  id: state.id,
  file_id: state.file_id,
  name: state.name,
  url: state.url,
  status: Number(state.status),
  optionOpenInNewWindow: state.optionOpenInNewWindow,
  optionOpenInBrowser: state.optionOpenInBrowser,
  mobile_file_id: state.mobile_file_id,
  activeFrom: state.activeFrom ? state.activeFrom.getTime() : null,
  activeUntil: state.activeUntil ? state.activeUntil?.getTime() : null,
  i18n: state.i18n.map((item) => ({
    language_code: item.language_code,
    file_id: item.file_id,
    mobile_file_id: item.mobile_file_id,
    button_text: item.button_text
  })),
  banner_type_id: state.selectedBannerTypeId
    ? Number(state.selectedBannerTypeId)
    : null,
  has_button: state.has_button,
  button_color: state.button_color ?? null,
  button_text: state.button_text ?? null
})

export const mapApiDataToState = (data: Banner): BannerFormState => ({
  id: data.id,
  file_id: data.file_id,
  mobile_file_id: data.mobile_file_id,
  name: data.name,
  url: data.url,
  status: Number(data.status),
  optionOpenInNewWindow: data.optionOpenInNewWindow,
  optionOpenInBrowser: data.optionOpenInBrowser,
  bannerSlide: data.bannerSlide,
  bannerMobileSlide: data.bannerMobileSlide,
  activeFrom: data.activeFrom ? new Date(data.activeFrom as Date) : undefined,
  activeUntil: data.activeUntil
    ? new Date(data.activeUntil as Date)
    : undefined,
  bannerType: data.bannerType,
  i18n: data.i18n.map((item) => ({
    ...BANNER_TRANSLATION_DEFAULT_STATE,
    ...item
  })),
  languages: data.i18n?.map((item) => item.language_code).concat([ 'По-умолчанию' ]),
  selectedBannerTypeId: data.bannerType?.id
    ? Number(data.bannerType?.id)
    : undefined,
  has_button: data.has_button,
  button_color: data.button_color,
  button_text: data.button_text
})
